/**
 * The site's footer, resolved from site data.
 *
 * THE SINGLE COPY. It lived in apps/tenant-sites and was reachable only by the live renderer, so
 * the Studio preview had no way to show a footer at all: StudioBuilder sourced one from a footer
 * BLOCK on the home page, and every home look materialises preset blocks that omit nav and footer
 * by design. The result was a footer edit that looked like it had done nothing while editing and
 * was perfectly correct once published — the worst shape a bug can take, because the agent's own
 * eyes tell them the product is broken.
 *
 * Shared rather than copied. A second implementation would drift, and this codebase already paid
 * for that once: a planner prompt taught `headline` for a hero whose prop is `heading`, because
 * the prop catalogue lived in a file the planner could not see.
 */

import type { FooterBlockProps, FooterDesign, FooterSiteData } from '@realsite/blocks'
import { resolveHeaderOverHero, type HeaderOverHero } from './headerLooks'

// Structural on purpose — see the note in the header. Neither app's SiteConfig is imported,
// so both can call this with their own.
export interface FooterSourceConfig {
  /** 'home-sgr' sites showed the Simon Gray footer before the footer section stored a design. */
  template?: string | null
  pages?: unknown
  navigation?: unknown
  legal_pages?: LegalPageRow[]
  // No index signature: an interface without one is not assignable to an interface WITH one, so
  // adding it here would reject both apps' SiteConfig at every call site.
  // Only the fields the footer actually reads are named. Naming these and nothing else is what
  // lets both apps pass their own SiteConfig without either type being imported here.
  brand?: {
    company_name?: string | null
    tagline?: string | null
    logo_url?: string | null
    logo_dark_url?: string | null
    social?: Record<string, string | null | undefined> | null
  } | null
  /** Business details, already resolved brand-first by the gateway (brands.default_* → profile). */
  profile?: {
    phone?: string | null
    email?: string | null
    address?: string | null
    social?: Record<string, string | null | undefined> | null
  } | null
  style?: {
    color_primary?: string | null
    colors?: { text_on_dark?: string | null } | null
  } | null
  /**
   * The OLD footer setting, read only for a site whose values have not been moved into its footer
   * section yet (api-gateway lib/footer-one-home.ts moves them, and removes this key, shortly
   * after it starts). See `withUnmovedSettings`.
   */
  footer?: unknown
}

type SiteConfig = FooterSourceConfig

/**
 * A site whose old footer setting has not been moved yet shows what it showed before: the old
 * copyright line, headings, show-logo choice, and phone/email (shown ahead of Brand Manager's, as
 * this-site-only values). Only the Simple and Simon Gray designs ever read that setting. The move
 * writes the same values into the section, so this is a no-op once it has run; for a site the move
 * skipped (or with the move paused) the footer keeps looking the same rather than losing them.
 */
function withUnmovedSettings(props: Partial<FooterBlockProps>, legacy: unknown): Partial<FooterBlockProps> {
  if (!legacy || typeof legacy !== 'object' || Array.isArray(legacy)) return props
  const old = legacy as Record<string, unknown>
  const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined)
  const out: Partial<FooterBlockProps> = { ...props }
  if (!text(out.copyright) && text(old.copyright)) out.copyright = text(old.copyright)
  if (!text(out.quickLinksHeading) && text(old.quick_links_heading)) out.quickLinksHeading = text(old.quick_links_heading)
  if (!text(out.contactHeading) && text(old.contact_heading)) out.contactHeading = text(old.contact_heading)
  if (out.showLogo === undefined && old.show_logo === false) out.showLogo = false
  const owned = new Set(Array.isArray(out.siteOwned) ? out.siteOwned : [])
  for (const [field, key] of [['phone', 'contact_phone'], ['email', 'contact_email']] as const) {
    const v = text(old[key])
    if (v && !owned.has(field)) { out[field] = v; owned.add(field) }
  }
  if (owned.size) out.siteOwned = Array.from(owned)
  return out
}

export interface FooterNavLink {
  label: string
  href: string
  open_in_new_tab?: boolean
}

/**
 * Footer links = nav items flagged `location: 'footer'` (flat — nesting is ignored for footers),
 * sorted by sort_order. Empty when none are set, so a site with no footer items keeps its current
 * footer (block links / legacy chrome) unchanged. Read from the RAW siteConfig.navigation (before
 * resolveSiteMenu strips footer items for the header).
 */
export function resolveFooterNav(siteConfig: SiteConfig | null | undefined): FooterNavLink[] {
  const nav = Array.isArray(siteConfig?.navigation) ? siteConfig!.navigation : []
  return nav
    .filter((n) => n.location === 'footer')
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((n) => ({ label: n.label, href: n.url, open_in_new_tab: n.open_in_new_tab }))
}

// Footer unification (mirrors siteMenu.ts): the live layout renders the SAME footer the
// studio previews — the home page's footer section, in whichever design it has — so "edit the
// footer in the studio" === "what ships".

/**
 * The props of the home page's chrome block of this type (the ONE site-wide header / footer), or
 * null. Published blocks first, the draft only when nothing is published — what visitors see.
 */
function homeChromeProps(siteConfig: SiteConfig | null | undefined, type: 'nav' | 'footer'): Record<string, unknown> | null {
  const pages = Array.isArray(siteConfig?.pages) ? siteConfig!.pages : []
  const home =
    pages.find((p) => (p as { is_home_page?: unknown }).is_home_page) ??
    pages.find((p) => {
      const s = String((p as { slug?: string }).slug ?? '').replace(/^\/+/, '')
      return s === '' || s === 'home'
    }) ??
    pages[0]
  const rec = home as { blocks?: unknown; draft_blocks?: unknown } | undefined
  const blocks = Array.isArray(rec?.blocks)
    ? rec!.blocks
    : Array.isArray(rec?.draft_blocks)
      ? rec!.draft_blocks
      : []
  const block = (blocks as Array<{ type?: string; props?: unknown }>).find((b) => b?.type === type)
  if (!block || typeof block.props !== 'object' || block.props === null) return null
  return block.props as Record<string, unknown>
}

/**
 * The header's custom colours (plan E1): the home page's nav block `backgroundColor` / `textColor`,
 * raw — the headers sanitize and resolve contrast with `resolveHeaderColors` (@realsite/blocks).
 * Null when the home has no nav block or neither colour is set, so every header without them
 * renders exactly as before.
 */
export function resolveSiteHeaderColors(
  siteConfig: SiteConfig | null | undefined,
): { backgroundColor?: string; textColor?: string } | null {
  const props = homeChromeProps(siteConfig, 'nav')
  if (!props) return null
  const pick = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined)
  const backgroundColor = pick(props.backgroundColor)
  const textColor = pick(props.textColor)
  if (!backgroundColor && !textColor) return null
  return { ...(backgroundColor ? { backgroundColor } : {}), ...(textColor ? { textColor } : {}) }
}

/**
 * How the header sits over the home hero (headerLooks.ts `HeaderOverHero`): the home page's nav
 * section `overHero`, the same section that holds the header's colours. 'auto' when there is no
 * such section or no value, so every header without one renders exactly as before.
 */
export function resolveSiteHeaderOverHero(siteConfig: SiteConfig | null | undefined): HeaderOverHero {
  return resolveHeaderOverHero(homeChromeProps(siteConfig, 'nav')?.overHero)
}

const FOOTER_DESIGNS: readonly FooterDesign[] = ['columns', 'simple', 'simon-gray']

/**
 * Which look the site's footer has. The footer section's stored `design` when it has one;
 * otherwise exactly the choice the live layout always made, so a footer nobody has touched keeps
 * its look: the Simon Gray template's footer on 'home-sgr' sites, the column footer when the home
 * page has a footer section with a company name, and the simple footer when it has none.
 */
export function resolveFooterDesign(
  siteConfig: SiteConfig | null | undefined,
  block: Record<string, unknown> | null = homeChromeProps(siteConfig, 'footer'),
): FooterDesign {
  const stored = block?.design
  if (typeof stored === 'string' && (FOOTER_DESIGNS as readonly string[]).includes(stored)) return stored as FooterDesign
  if (siteConfig?.template === 'home-sgr') return 'simon-gray'
  if (typeof block?.companyName === 'string' && block.companyName.trim()) return 'columns'
  return 'simple'
}

export interface ResolveSiteFooterOptions {
  /**
   * The footer section to resolve, when the caller holds a newer copy than `siteConfig.pages`
   * (the Studio's open draft). Defaults to the home page's published footer section.
   */
  block?: Record<string, unknown> | null
  /** simple design: the Quick Links shown when the site has no footer menu (plan-aware, host-built). */
  quickLinks?: FooterNavLink[]
}

const socialLinksFrom = (social: Record<string, string | null | undefined> | null | undefined) =>
  social
    ? [
        { label: 'Instagram', href: social.instagram },
        { label: 'Facebook', href: social.facebook },
        { label: 'LinkedIn', href: social.linkedin },
        { label: 'YouTube', href: social.youtube },
      ].filter((l): l is { label: string; href: string } => Boolean(l.href))
    : []

/**
 * THE site footer: the home page's footer section, resolved for rendering — every design, every
 * page, the live site and the Studio preview alike. Never null: a site always has a footer.
 *
 * ONE source of truth. The footer section on the home page holds everything about the footer
 * that belongs to this site (its design, link columns, copyright line, headings, disclaimer,
 * colours); Brand Manager holds the business details (name, tagline, phone, email, address,
 * socials — the gateway resolves them onto `brand` / `profile`); the site menu holds the
 * bottom-row links (`location: 'footer'`). The footer used to keep its copyright, headings and
 * a phone number in `siteConfig.footer` as well, read by two of the three footer designs only.
 *
 * Business details follow the brand, in one order for every design: a field the tenant set for
 * THIS SITE ONLY (`siteOwned`, the Studio chat's "This site only") shows the section's own value;
 * otherwise Brand Manager's; otherwise the section's own value. The simple and Simon Gray designs
 * used to put `siteConfig.footer.contact_phone` first instead, so one site could show a different
 * phone number depending on its footer design.
 */
export function resolveSiteFooter(
  siteConfig: SiteConfig | null | undefined,
  opts: ResolveSiteFooterOptions = {},
): FooterBlockProps {
  const raw = (opts.block !== undefined ? opts.block : homeChromeProps(siteConfig, 'footer')) ?? {}
  const design = resolveFooterDesign(siteConfig, raw)
  const props = design === 'columns'
    ? (raw as Partial<FooterBlockProps>)
    : withUnmovedSettings(raw as Partial<FooterBlockProps>, siteConfig?.footer)
  const brand = siteConfig?.brand
  const profile = siteConfig?.profile
  const owned = new Set(Array.isArray(props.siteOwned) ? props.siteOwned : [])
  const columns = design === 'columns'

  /** A business detail: this site's own value, else the brand's, else the section's. */
  const detail = (field: 'companyName' | 'tagline' | 'phone' | 'email' | 'address', brandValue: string | null | undefined) => {
    const own = typeof props[field] === 'string' ? (props[field] as string) : undefined
    if (owned.has(field)) return own
    return brandValue?.trim() || own
  }

  const out: FooterBlockProps = {
    ...(props as FooterBlockProps),
    design,
    companyName: detail('companyName', brand?.company_name) ?? '',
    tagline: detail('tagline', brand?.tagline),
    phone: detail('phone', profile?.phone),
    email: detail('email', profile?.email),
    address: detail('address', profile?.address),
    navLinks: resolveFooterNav(siteConfig).map((l) => ({ label: l.label, href: l.href })),
    legalPages: legalLinksFromConfig(siteConfig).map((l) => ({ label: l.label, href: l.href })),
  }
  // Brand Manager's socials drive the column footer when it has any; otherwise the section keeps
  // its own. The other two designs list the profile's (brand-first, resolved by the gateway).
  const brandSocial = socialLinksFrom(brand?.social)
  if (columns && brandSocial.length > 0) out.socialLinks = brandSocial
  for (const k of ['tagline', 'phone', 'email', 'address'] as const) if (out[k] === undefined) delete out[k]

  if (!columns) {
    const nav = Array.isArray(siteConfig?.navigation) ? (siteConfig!.navigation as Array<Record<string, unknown>>) : []
    const pages = Array.isArray(siteConfig?.pages) ? (siteConfig!.pages as Array<Record<string, unknown>>) : []
    const site: FooterSiteData = {
      logoUrl: brand?.logo_url ?? null,
      logoDarkUrl: brand?.logo_dark_url ?? null,
      primaryColor: siteConfig?.style?.color_primary ?? null,
      textOnDark: siteConfig?.style?.colors?.text_on_dark ?? null,
      quickLinks: (opts.quickLinks ?? []).map((l) => ({ label: l.label, href: l.href })),
      headerLinks: nav
        .filter((n) => !n.parent_id)
        .slice()
        .sort((a, b) => Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0))
        .map((n) => ({ label: String(n.label ?? ''), href: String(n.url ?? '') })),
      authoredLegalLinks: pages
        .filter((p) => p.is_published && (p.slug === '/privacy-policy' || p.slug === '/terms'))
        .map((p) => ({ label: String(p.name ?? ''), href: String(p.slug) })),
      social: profile?.social ?? null,
    }
    out.site = site
  }
  return out
}

/**
 * The colour the footer paints, so the space a phone shows past the end of the page can match it.
 */
export function resolveFooterBackground(siteConfig: SiteConfig | null | undefined): string {
  const footer = resolveSiteFooter(siteConfig)
  const pinned = footer.backgroundColor?.trim()
  if (pinned) return pinned
  if (footer.design === 'simon-gray') return siteConfig?.style?.color_primary || 'var(--color-primary)'
  if (footer.design === 'simple') return '#111827'
  return 'var(--color-secondary)'
}

// ── Guaranteed legal links ────────────────────────────────────────────────────

export interface LegalFooterLink {
  label: string
  href: string
}

/**
 * The legal links every footer must carry, merged into whatever links it already has.
 *
 * Privacy Policy and Terms of Use are guaranteed rather than conditional on a page row
 * existing. A site whose agent published a generated privacy policy but never created a page
 * of that slug used to get no footer link at all on the SGR template — the document was live
 * and reachable, and nothing on the site pointed at it. Neither slug can 404: the [slug] route
 * serves the published document, then the agent's own page, then starter text.
 *
 * An existing link always wins, so an agent's own title and casing are preserved.
 *
 * Deduping is by CANONICAL slug, not string equality: `/privacy` and `/privacy-policy` are the
 * same page (the route maps both), and the footers historically disagreed about which to link.
 * Comparing raw hrefs would show both.
 */
const LEGAL_ALIASES: Record<string, string> = {
  'privacy': 'privacy',
  'privacy-policy': 'privacy',
  'terms': 'terms',
  'terms-of-use': 'terms',
  'terms-of-service': 'terms',
}

export function canonicalLegalSlug(href: string): string {
  const slug = href.replace(/^\/+|\/+$/g, '').toLowerCase()
  return LEGAL_ALIASES[slug] ?? slug
}

export interface LegalFooterLinkWithTarget extends LegalFooterLink {
  /** An uploaded document opens in a new tab; a page this site renders does not. */
  external?: boolean
}

/**
 * The legal links this site's catalogue says it has, falling back to the two every site has
 * always linked when the gateway is older than the catalogue (or the lookup failed — legal pages
 * must never be the reason a footer renders empty).
 */
/** A row of the platform's legal-page catalogue, as the site config carries it. */
export interface LegalPageRow {
  /** Catalogue identifier, e.g. 'privacy' or 'ny-sop'. Carried, not read here. */
  kind?: string
  label: string
  /** Path on this site, without a leading slash. */
  slug: string
  /** An uploaded document to open, or null when the site renders the page itself. */
  url: string | null
}

export function legalLinksFromConfig(
  siteConfig: { legal_pages?: LegalPageRow[] } | null | undefined,
): LegalFooterLinkWithTarget[] {
  const pages = siteConfig?.legal_pages
  if (!pages || pages.length === 0) {
    return [
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Terms of Use', href: '/terms' },
    ]
  }
  return pages.map((p) => ({
    label: p.label,
    href: p.url ?? `/${p.slug.replace(/^\/+/, '')}`,
    external: Boolean(p.url),
  }))
}

/**
 * The legal links a footer still needs once its other links are on the page: a link the footer
 * already shows (menu item, quick link) is not repeated, compared by canonical slug — so a footer
 * menu item to `/privacy` and the catalogue's `/privacy-policy` do not both appear.
 */
export function legalLinksNotAlreadyLinked<T extends LegalFooterLink>(
  legalLinks: T[],
  alreadyLinked: Array<{ href: string }>,
): T[] {
  const seen = new Set(alreadyLinked.map((l) => canonicalLegalSlug(l.href)))
  return legalLinks.filter((l) => !seen.has(canonicalLegalSlug(l.href)))
}

export function withGuaranteedLegalLinks<T extends LegalFooterLink>(
  links: T[],
  guaranteed: LegalFooterLink[] = [
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms of Use', href: '/terms' },
  ],
): (T | LegalFooterLink)[] {
  const seen = new Set(links.map((l) => canonicalLegalSlug(l.href)))
  const out: (T | LegalFooterLink)[] = [...links]
  for (const link of guaranteed) {
    const key = canonicalLegalSlug(link.href)
    if (seen.has(key)) continue
    seen.add(key)
    out.push(link)
  }
  return out
}
